import { HttpClient } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { tap } from "rxjs/operators";
import { BASE_API_URL } from "../../../core/environment.tokens";
import { TenantStatisticsResponse } from "./tenant-statistics-response.model";

interface ICachedStatistics {
  data: any;
  lastCalled: number;
}

@Injectable({
  providedIn: "root",
})
export class ChartLakeStatisticsService {
  private readonly apiUrl: string;
  private readonly cacheDurationMs = 30 * 60 * 1000; // 30 minutes
  private readonly localStorageKeyPrefix = "chartLakeStatistics_";

  constructor(
    private http: HttpClient,
    @Inject(BASE_API_URL) baseUrl: string
  ) {
    this.apiUrl = `${baseUrl}m360/tenant/statistics`;
  }

  getTenantStatistics(tenantId: string): Observable<TenantStatisticsResponse> {
    const key = this.localStorageKeyPrefix + tenantId;
    const cachedRaw = localStorage.getItem(key);
    if (cachedRaw) {
      try {
        const cached: ICachedStatistics = JSON.parse(cachedRaw);
        if (cached && cached.data && cached.data.lastUpdated) {
          const lastUpdated = new Date(cached.data.lastUpdated).getTime();
          const now = Date.now();
          const cacheExpired = (now - cached.lastCalled) > this.cacheDurationMs;
          const recentUpdate = (now - lastUpdated) < this.cacheDurationMs;
          const updatedAfterLastCall = lastUpdated > cached.lastCalled;

          if (!isNaN(lastUpdated) &&
            (!cacheExpired || recentUpdate || updatedAfterLastCall)) {
            return of(cached.data);
          }
        }
      } catch { }
    }
    return this.http.get<TenantStatisticsResponse>(`${this.apiUrl}?tenantId=${tenantId}`).pipe(
      tap(data => {
        const lastCalled = Date.now();
        const cacheObj: ICachedStatistics = { data, lastCalled };
        localStorage.setItem(key, JSON.stringify(cacheObj));
      })
    );
  }
}
